import { defineStore } from "pinia";
import { CommonHttp } from "@/models/interfaces/http/common.http.request";
import { reactive } from "vue";
import { rootStore } from "./index";
import { generalStore } from "./general";
import { AnalyticsChartIdentity, AnalyticsElementIdentity, ChartElementSettingsBase, ChartSettingsBase, GlobalTableFormulasList, LinearChartSettingsBase, PieChartBundleSettingsBase, TableElementWithFormulas } from "@/models/interfaces/entity-settings/charts/charts";
import { AnalyticsV2ChartType, AnalyticsV2ElementType, ChartSettingsBaseDescriptor } from "@/models/etc/types";
import { settingsStore } from "./settings";
import { ProjectBarChartBundleSettings, ProjectBarChartSettings, ProjectLinearChartCurveSettings, ProjectLinearChartSettings, ProjectPieChartBundleSettings, ProjectPieChartSettings } from "@/models/interfaces/entity-settings/charts/project-charts";
import { ClientBarChartBundleSettings, ClientBarChartSettings, ClientLinearChartCurveSettings, ClientLinearChartSettings, ClientPieChartBundleSettings, ClientPieChartSettings } from "@/models/interfaces/entity-settings/charts/client-charts";
import { FileBarChartBundleSettings, FileBarChartSettings, FileLinearChartCurveSettings, FileLinearChartSettings, FilePieChartBundleSettings, FilePieChartSettings } from "@/models/interfaces/entity-settings/charts/file-charts";
import { JobBarChartBundleSettings, JobBarChartSettings, JobLinearChartCurveSettings, JobLinearChartSettings, JobPieChartBundleSettings, JobPieChartSettings } from "@/models/interfaces/entity-settings/charts/job-charts";
import { PersonBarChartBundleSettings, PersonBarChartSettings, PersonLinearChartCurveSettings, PersonLinearChartSettings, PersonPieChartBundleSettings, PersonPieChartSettings } from "@/models/interfaces/entity-settings/charts/person-charts";
import { BaseItemTypes } from "@/components/dnd/ItemTypes";
import { BaseListItem, getIdentityKind } from "@/components/common/base-list/interfaces/ListTypes";
import { auxStore } from "./auxillary";
import { foreachRecursive, mapRecursive } from "@/models/helpers/recursive";
import { CommonEntity } from "@/models/interfaces/common";
import { WorkloadBarChartBundleSettings, WorkloadBarChartSettings, WorkloadLinearChartCurveSettings, WorkloadLinearChartSettings, WorkloadPieChartBundleSettings, WorkloadPieChartSettings } from "@/models/interfaces/entity-settings/charts/workload-charts";
import { validateCharts } from "./validation";
import { StorageItemBarChartBundleSettings, StorageItemBarChartSettings, StorageItemLinearChartCurveSettings, StorageItemLinearChartSettings, StorageItemMovementBarChartBundleSettings, StorageItemMovementBarChartSettings, StorageItemMovementLinearChartCurveSettings, StorageItemMovementLinearChartSettings, StorageItemMovementPieChartBundleSettings, StorageItemMovementPieChartSettings, StorageItemPieChartBundleSettings, StorageItemPieChartSettings } from "../models/interfaces/entity-settings/charts/storage-charts";
import { AnalyticsElementOptionsDto } from "../models/interfaces/entity-settings/filters/filters";

/**
 * charts methods store
 */

const API_URL = process.env.VUE_APP_API_URL

// base chart settings types
export type LinearTypes = ProjectLinearChartSettings | ClientLinearChartSettings | JobLinearChartSettings | 
PersonLinearChartSettings | FileLinearChartSettings | WorkloadLinearChartSettings | StorageItemLinearChartSettings | StorageItemMovementLinearChartSettings;

export type LinearCurveTypes = ProjectLinearChartCurveSettings | ClientLinearChartCurveSettings | JobLinearChartCurveSettings | 
PersonLinearChartCurveSettings | FileLinearChartCurveSettings | WorkloadLinearChartCurveSettings | StorageItemLinearChartCurveSettings | 
StorageItemMovementLinearChartCurveSettings;

export type PieTypes = ProjectPieChartBundleSettings | ClientPieChartBundleSettings | JobPieChartBundleSettings |
PersonPieChartBundleSettings | FilePieChartBundleSettings | WorkloadPieChartBundleSettings| StorageItemPieChartBundleSettings | StorageItemMovementPieChartBundleSettings;

export type BarTypes = ProjectBarChartBundleSettings | ClientBarChartBundleSettings | JobBarChartBundleSettings |
PersonBarChartBundleSettings | FileBarChartBundleSettings | WorkloadBarChartBundleSettings | StorageItemBarChartBundleSettings | StorageItemMovementBarChartBundleSettings;

export type SectorPieTypes = ProjectPieChartSettings | ClientPieChartSettings | JobPieChartSettings |
PersonPieChartSettings | FilePieChartSettings | WorkloadPieChartSettings| StorageItemPieChartSettings | StorageItemMovementPieChartSettings;

export type SectorBarTypes = ProjectBarChartSettings | ClientBarChartSettings | JobBarChartSettings |
PersonBarChartSettings | FileBarChartSettings | WorkloadBarChartSettings | StorageItemBarChartSettings | StorageItemMovementBarChartSettings;

export type ClientProjectLinearTypes = ProjectLinearChartSettings | ClientLinearChartSettings;
export type ClientProjectLinearCurveTypes = ProjectLinearChartCurveSettings | ClientLinearChartCurveSettings;
export type ClientProjectSectorTypes = ClientBarChartSettings | ProjectBarChartSettings |
ClientPieChartSettings | ProjectPieChartSettings;

export type ClientProjectPieTypes = ClientPieChartBundleSettings | ProjectPieChartBundleSettings;
export type ClientProjectBarTypes = ClientBarChartBundleSettings | ProjectBarChartBundleSettings;

export interface CommonEntityWithPureId extends CommonEntity {
  PureId: number;
}
export interface AdaptedFormulasOption {
  Identity: AnalyticsChartIdentity;
  ChildFormulas: AdaptedFormulasOption[];
  Entity: CommonEntityWithPureId;
}

// Формулы принадлежат таблице, поэтому годится только родитель-элемент таблицы.
// У папки в Identity лежит FolderId + Segment, а у ссылки в отчёте Identity нет вовсе.
// Сверка по !== undefined, так как Kind 0 это выборка, а Destination 0 это проект.
function parentElementIdentity(parent: any): AnalyticsElementIdentity | undefined {
  const ident = parent?.Identity
  if (ident && ident.ElementId !== undefined && ident.Kind !== undefined && ident.Destination !== undefined) {
    return ident as AnalyticsElementIdentity
  }

  // Вторая валидная форма того же табличного элемента: смапленный узел дерева. Identity у него нет,
  // поля разложены по Id / elType / SrcType / DestType. Типы, кроме выборки, проекции и списка,
  // сюда не пускаем: у графика getIdentityKind отдал бы List.
  const elType = parent?.elType
  if (elType !== BaseItemTypes.SELECTION && elType !== BaseItemTypes.PROJECTION && elType !== BaseItemTypes.LIST) {
    return undefined
  }

  const kind = getIdentityKind(parent as BaseListItem)
  if (kind === undefined || parent.Id === undefined || parent.DestType === undefined) {
    return undefined
  }

  return {
    ElementId: parent.Id,
    Kind: kind,
    // Source есть только у проекций, у выборок и списков его нет.
    Source: parent.SrcType !== undefined && parent.SrcType !== null ? parent.SrcType : undefined,
    Destination: parent.DestType,
  }
}

export type GraphSettingsState = {
    loading: boolean;
    // indication that not to update the model on @Watch
    mutating: boolean;
    saving: boolean;
    // ignore the request result and don't mutate the model after the request is finished
    stop_request: boolean;
    // current selected graph parameters
    selectedSettings: ChartSettingsBase;
    selectedGlobalFormulas: AdaptedFormulasOption[][];
    // last update successful or not
    chartSettingsUpdated: boolean;
    chartElementsUpdated: boolean;

    // model validation errors
    chartsValidation: any[];
    reqQueue: Map<string, AbortController>;
};

export const graphSettingsStore = defineStore({
  id: "graphSettingsStore",
  state: () =>
    ({
        loading: false,
        // indication that not to update the model on @Watch
        mutating: false,
        saving: false,
        // ignore the request result and don't mutate the model after the request is finished
        stop_request: false,
        // reactive filters change and update directly from interface
        selectedSettings: {} as ChartSettingsBase,
        selectedGlobalFormulas: reactive([]),
        chartSettingsUpdated: true,
        chartElementsUpdated: true,
        chartsValidation: reactive([]),
        reqQueue: new Map(),
    } as GraphSettingsState),

  actions: {
    abortAllPending() {
      this.reqQueue.forEach((controller) => controller.abort())
      this.reqQueue.clear()
    },

    stopRequest() {
      this.stop_request = true
      this.clearSettings()
    },

    clearChartsValidation() {
      this.chartsValidation.splice(0);
      this.stop_request = false;
    },

    // debounced refresh actions for using after update (if needed)

    setMutating(payload: boolean) {
      this.mutating = payload
    },

    setSettingsUpdated(val: boolean) {
      this.chartSettingsUpdated = val
    },

    setElementsUpdated(val: boolean) {
      this.chartElementsUpdated = val
    },

    async getGraphSettings(arg: AnalyticsChartIdentity, parent: BaseListItem, no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.chartsValidation.length > 0) {
        return
      }
      
      const rootstore = rootStore()
      const setstore = settingsStore()

      const entkey = 'chartSettings'
      this.reqQueue.get(entkey)?.abort()
      this.reqQueue.delete(entkey)
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = !no_indication
        this.saving = false;
        const response = await http.post(`Analytics/Charts/GetSettings`, arg, { signal: controller.signal });

        this.reqQueue.delete(entkey)
        if (response.status === 200) {
          const genstore = generalStore()
          auxStore().recordAnalyticsReadChart(genstore.selectedItem)
          // don't update data if genstore is loading again because this data is obsolette
          if (!genstore.isLoading && !this.stop_request) {
            this.setMutating(true)

            const { data } = response;
            // reset old
            // reactive settings reset
            this.selectedSettings = reactive({} as ChartSettingsBase);
            this.selectedSettings = reactive(data);

            const auxstore = auxStore()
            const parentIdentity = parentElementIdentity(parent)
            if (auxstore && parentIdentity &&
                [AnalyticsV2ElementType.Project, AnalyticsV2ElementType.Client].includes(parentIdentity.Destination)) {
              const fList: GlobalTableFormulasList = (await auxstore.getGlobalFormulasList(parentIdentity)).data
              let incf = 0;
              if (fList && (fList.ChartOwnerRootElement != null || fList.OtherRootElements != null)) {
                this.selectedGlobalFormulas = [];
                const linear: any[] = [];

                foreachRecursive(function a(el: any, el_to_find: any) {
                  if (!el.Id || !el.Entity || !el.Entity.Id) { incf++ }
                  
                  if (el.Id || el.Entity) {
                    linear.push({
                      // @ts-ignore
                      Identity: el.Identity,
                      Entity: {
                        Id: el.Id ? (el.Identity ? el.Identity.ElementId : 'f')+'-'+el.Id : (el.Entity ? (el.Identity ? el.Identity.ElementId : 'f')+'-'+el.Entity.Id : 'f'+incf),
                        Name: el.Name || el.Entity?.Name,
                        PureId: el.Id ? el.Id : (el.Entity ? el.Entity.Id : 'f'+incf),
                      },
                      ChildFormulas: []
                    })
                  }

                  foreachRecursive(a, el, [...el.Formulas.map((f: any) => {
                    return {
                      // @ts-ignore
                      Identity: el.Identity,
                      Entity: {
                        Name: f.Name,
                        Id: f.Id,
                      },
                      ChildElements: [],
                      Formulas: []
                    }
                  }), ...el.ChildElements])
                // @ts-ignore
                }, undefined, fList.ChartOwnerRootElement ? [fList.ChartOwnerRootElement] : [])
                
                this.selectedGlobalFormulas.push(linear);
                this.selectedGlobalFormulas.push(mapRecursive(function a(el: any, parent: any) {
                  if (!el.Id || !el.Entity || !el.Entity.Id) { incf++ }
                  return {
                    Identity: el.Identity,
                    Entity: {
                      Id: el.Id ? (el.Identity ? el.Identity.ElementId : 'f')+'-'+el.Id : (el.Entity ? (el.Identity ? el.Identity.ElementId : 'f')+'-'+el.Entity.Id : 'f'+incf),
                      Name: el.Name || el.Entity?.Name,
                      PureId: el.Id ? el.Id : (el.Entity ? el.Entity.Id : 'f'+incf),
                    },
                    ChildFormulas: mapRecursive(a, el, [...el.Formulas.map((f: any) => {
                      return {
                        Identity: el.Identity,
                        Entity: {
                          Name: f.Name,
                          Id: f.Id
                        },
                        ChildElements: [],
                        Formulas: []
                      }
                    }), ...el.ChildElements])
                  } as AdaptedFormulasOption
                // @ts-ignore
                }, undefined, fList.OtherRootElements || []))

                console.log('has formulas!')
                console.log(this.selectedGlobalFormulas)
              } else {
                this.selectedGlobalFormulas = reactive([])
              }
            } else {
              this.selectedGlobalFormulas = reactive([])
            }

            // true because now the state has just been refreshed
            this.setSettingsUpdated(true)
            this.setElementsUpdated(true)

            // console.log('g1')
            // console.log(this.selectedSettings as LinearChartSettingsBase)
            // console.log('g2');
            setstore.setGraphColumns((this.selectedSettings as LinearChartSettingsBase).ColumnsSettings)
          }

          this.loading = false
          return { response_ok: true }
        }
      } catch (e) {
        this.reqQueue.delete(entkey)
        if ((e as any)?.name === 'CanceledError' || (e as any)?.code === 'ERR_CANCELED') return { response_ok: false }
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    /**
     * updateSettings method. Backend API method for updating graph settings
     * @param arg - new parameters and item Id
     * @param element_i - child element index for update; if -1 then main charg updates; otherwise element_i-th element;
     * @returns object with response_ok if successful, error otherwise
     */
    async updateSettings(arg: AnalyticsChartIdentity, element_i: number, no_indication: boolean = false) {
      // don't save erroneous data
      if (this.chartsValidation.length) {
        return
      }

      const rootstore = rootStore()
      this.saving = true;
      const http = new CommonHttp()

      // try {
        let http_url = "";
        let params = {}
        let all_elements_saved = true;

        if (element_i < 0) {
          const ds = new Date((this.getCurrentSettings as LinearChartSettingsBase).DateRangeStart);
          let de = (this.getCurrentSettings as LinearChartSettingsBase).DateRangeEnd; 

          if ((this.getCurrentSettings as LinearChartSettingsBase).StepType != 5) {
            ds.setHours(12);
            if (de) {
              de = new Date(de)
              de.setHours(12);
            }
          }

          switch (arg.ChartType) {
            case AnalyticsV2ChartType.Linear:
              params = {
                ChartId: arg.ChartId,
                Destination: arg.Destination,
                // LinearFunction: (this.getCurrentSettings as LinearChartSettingsBase).LinearFunction,
                StepType: (this.getCurrentSettings as LinearChartSettingsBase).StepType,
                StepLength: (this.getCurrentSettings as LinearChartSettingsBase).StepLength,
                DateRangeStart: ds,
                DateRangeEnd: de,
                DateRangeEndIsToday: (this.getCurrentSettings as LinearChartSettingsBase).DateRangeEndIsToday,
                ChartWidth: (this.getCurrentSettings as LinearChartSettingsBase).ChartWidth,
                ChartElementWidth: (this.getCurrentSettings as LinearChartSettingsBase).ChartElementWidth
              }

              /* if ([ChartSettingsBaseDescriptor.ClientLinearChart, ChartSettingsBaseDescriptor.ProjectLinearChart]
              .includes((this.getCurrentSettings as LinearChartSettingsBase).Descriptor)) {
                params = { ...params, EvaluateFunction: (this.getCurrentSettings as ClientProjectLinearTypes).EvaluateFunction }
              }
              else */ if ((this.getCurrentSettings as LinearChartSettingsBase).Descriptor === ChartSettingsBaseDescriptor.FileLinearChart) {
                params = { ...params, FileFactFlags: (this.getCurrentSettings as FileLinearChartSettings).FactFlags }
              } else if ((this.getCurrentSettings as LinearChartSettingsBase).Descriptor === ChartSettingsBaseDescriptor.PersonLinearChart) {
                params = { ...params, PersonFactFlags: (this.getCurrentSettings as PersonLinearChartSettings).FactFlags }
              }
              http_url = `Analytics/Charts/Linear/EditSettings`;
              break;
            case AnalyticsV2ChartType.Pie:
              params = {
                BundleId: arg.ChartId,
                Destination: arg.Destination,
                DisplayType: (this.getCurrentSettings as PieChartBundleSettingsBase).DisplayType,
                ChartWidth: (this.getCurrentSettings as ChartSettingsBase).ChartWidth,
                ChartElementWidth: (this.getCurrentSettings as ChartSettingsBase).ChartElementWidth
              }

              /* if ([ChartSettingsBaseDescriptor.ClientPieChart, ChartSettingsBaseDescriptor.ProjectPieChart]
              .includes((this.getCurrentSettings as LinearChartSettingsBase).Descriptor)) {
                params = { ...params, EvaluateFunction: (this.getCurrentSettings as ClientProjectSectorTypes).EvaluateFunction }
                params = { ...params, ParameterLink: (this.getCurrentSettings as ClientProjectPieTypes).ParameterLink }
              } */

              http_url = `Analytics/Charts/Pie/EditBundleSettings`;
              break;
            case AnalyticsV2ChartType.Bar:
              params = {
                BundleId: arg.ChartId,
                Destination: arg.Destination,
                DisplayType: (this.getCurrentSettings as PieChartBundleSettingsBase).DisplayType,
                ChartWidth: (this.getCurrentSettings as ChartSettingsBase).ChartWidth,
                ChartElementWidth: (this.getCurrentSettings as ChartSettingsBase).ChartElementWidth
              }
              /* if ([ChartSettingsBaseDescriptor.ClientBarChart, ChartSettingsBaseDescriptor.ProjectBarChart]
              .includes((this.getCurrentSettings as LinearChartSettingsBase).Descriptor)) {
                params = { ...params, EvaluateFunction: (this.getCurrentSettings as ClientProjectTypes).EvaluateFunction }
                params = { ...params, ParameterLink: (this.getCurrentSettings as ClientProjectPieTypes).ParameterLink }
              } */
              http_url = `Analytics/Charts/Bar/EditBundleSettings`;
              break;
            default:
              http_url = ""
              break;
          }

          if (!http_url) {
            this.saving = false;
            throw new Error('Incorrect API URL');
          }
        } else {
          // update graph child elements
          const req_arr = (this.getCurrentSettings as any)[arg.ChartType === AnalyticsV2ChartType.Linear ? 'Curves' : (arg.ChartType === AnalyticsV2ChartType.Bar ? 'BarCharts' : 'PieCharts' )]
          
          for (let a = 0; a < req_arr.length; a++) {
            if (a === element_i) {
              const req_res = await this.updateGraphElement(req_arr[a], no_indication);
              all_elements_saved = all_elements_saved && req_res;
            }
          }
        }
        this.setElementsUpdated(all_elements_saved)

        if (element_i < 0) {
          const response = await http.put(http_url, params);
          // console.log(response)
          this.saving = false;
          
          if (response.status === 200 || response.status === 201) {

            if ((this.getCurrentSettings as ChartSettingsBase).Descriptor === ChartSettingsBaseDescriptor.StorageItemMovementLinearChart) {
              await this.editChartOptions({ chart: arg, opts: (this.getCurrentSettings as StorageItemMovementLinearChartSettings).Options })
            } else if ((this.getCurrentSettings as ChartSettingsBase).Descriptor === ChartSettingsBaseDescriptor.StorageItemMovementPieChart) {
              await this.editChartOptions({ chart: arg, opts: (this.getCurrentSettings as StorageItemMovementPieChartBundleSettings).Options })
            } else if ((this.getCurrentSettings as ChartSettingsBase).Descriptor === ChartSettingsBaseDescriptor.StorageItemMovementBarChart) {
              await this.editChartOptions({ chart: arg, opts: (this.getCurrentSettings as StorageItemMovementBarChartBundleSettings).Options })
            }
            
            this.setSettingsUpdated(true)
            this.saving = false;
            return { response_ok: true }
          } else {
            this.setSettingsUpdated(false)
          }
        }
        this.saving = false;

      this.saving = false;
      return { response_ok: false }
    },

    /**
     * addGraphElement method. Backend API method for adding a new curve or a sector to a graph
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async editChartOptions(arg: { chart: AnalyticsChartIdentity, opts: AnalyticsElementOptionsDto[] }, no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.chartsValidation.length) {
        return false
      }

      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        let http_url = "";
        let  params = {}
        switch (arg.chart.ChartType) {
          case AnalyticsV2ChartType.Linear:
          case AnalyticsV2ChartType.Bar:
          case AnalyticsV2ChartType.Pie:
            params = {
              ChartIdentity: arg.chart,
              Options: arg.opts
            }
            http_url = `Analytics/Charts/Options`;
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const response = await http.put(http_url, params);
        // console.log(response)
        if (response.status === 200 || response.status === 201) {
          // this.setElementsUpdated(true)

          return true
        } else {
          // this.setElementsUpdated(false)
        }
      } catch (e) {
        // this.setElementsUpdated(false)
        console.log(JSON.stringify(e))
      }

      return false
    },

    /**
     * addGraphElement method. Backend API method for adding a new curve or a sector to a graph
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async addGraphElement(arg: AnalyticsChartIdentity, no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.chartsValidation.length) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        let  params = {}
        switch (arg.ChartType) {
          case AnalyticsV2ChartType.Linear:
          case AnalyticsV2ChartType.Bar:
          case AnalyticsV2ChartType.Pie:
            params = arg
            http_url = `Analytics/Charts/CreateElement`;
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        // console.log('crg1')
        // console.log(params)
        // console.log('crg2')
        const response = await http.post(http_url, params);
        // console.log(response)
        if (response.status === 200 || response.status === 201) {
          this.setElementsUpdated(true)

          const genstore = generalStore()
            if (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
              genstore.selectedItem.elType === BaseItemTypes.GRAPH) {
                this.getGraphSettings({
                  ChartId: genstore.selectedItem.Id,
                  ChartType: genstore.selectedItem.elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : (
                    genstore.selectedItem.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Linear),
                  Destination: genstore.selectedItem.DestType
                }, genstore.selectedItem.parent)
            }

          return { response_ok: true }
        } else {
          this.setElementsUpdated(false)
        }
      } catch (e) {
        this.setElementsUpdated(false)
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * updateGraphElement method. Backend API method for editinfg a curve or a sector of a chart
     * @param arg - ChartElementSettingsBase
     * @returns object with response_ok if successful, error otherwise
     */
    async updateGraphElement(arg: ChartElementSettingsBase, no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.chartsValidation.length) {
        return false
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        const params = arg
        http_url = `Analytics/Charts/EditElementSettings`;

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        // console.log('crg1')
        // console.log(params)
        // console.log('crg2')
        const response = await http.put(http_url, params);
        // console.log(response)

        if (response.status === 200 || response.status === 201) {
          return true
        } else {
          return false
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return false
    },

    /**
     * removeGraphElement method. Backend API method for adding a new curve or a sector to a graph
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async removeGraphElement(el_id: number, descriptor: ChartSettingsBaseDescriptor, arg: AnalyticsChartIdentity,
        no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.chartsValidation.length) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        const params = {
          Descriptor: descriptor,
          ElementId: el_id
        }
        http_url = `Analytics/Charts/RemoveElement`;

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        // console.log('crg1')
        // console.log(params)
        // console.log('crg2')
        const response = await http.post(http_url, params);
        // console.log(response)
        if (response.status === 200 || response.status === 201) {
          this.setElementsUpdated(true)
          const genstore = generalStore()

          if (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
              genstore.selectedItem.elType === BaseItemTypes.GRAPH) {
                await this.getGraphSettings({
                  ChartId: genstore.selectedItem.Id,
                  ChartType: genstore.selectedItem.elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : (
                    genstore.selectedItem.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Linear),
                  Destination: genstore.selectedItem.DestType
                }, genstore.selectedItem.parent)
            }

          return { response_ok: true }
        } else {
          this.setElementsUpdated(false)
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * cloneGraphElement method. Backend API method for cloning a curve or a sector
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async cloneGraphElement(el_id: number, descriptor: ChartSettingsBaseDescriptor, arg: AnalyticsChartIdentity,
      no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.chartsValidation.length) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        const params = {
          Descriptor: descriptor,
          ElementId: el_id
        }
        http_url = `Analytics/Charts/CloneElement`;

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const response = await http.post(http_url, params);
        // console.log(response)
        if (response.status === 200 || response.status === 201) {
          this.setElementsUpdated(true)
          
          const genstore = generalStore()
              if (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
                genstore.selectedItem.elType === BaseItemTypes.GRAPH) {
                  this.getGraphSettings({
                    ChartId: genstore.selectedItem.Id,
                    ChartType: genstore.selectedItem.elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : (
                      genstore.selectedItem.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Linear),
                    Destination: genstore.selectedItem.DestType
                  }, genstore.selectedItem.parent)
              }

          return { response_ok: true }
        } else {
          this.setElementsUpdated(false)
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * changeChartFormulaPosition method. Backend API method for cloning a curve or a sector
     * @param f_id - formula id, chartId - chart Id, descriptor - chart settings-base descriptorm newpos - desired position
     * @returns object with response_ok if successful, error otherwise
     */
    async changeChartFormulaPosition(f_id: number, descriptor: ChartSettingsBaseDescriptor, chartId: number, newpos: number) {
      // don't refresh erroneous data
      if (this.chartsValidation.length) {
        return
      }
      
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        const params = {
          ElementId: chartId,
          ElementDescriptor: descriptor,
          FormulaId: f_id,
          DesiredPosition: newpos
        }
        http_url = `Analytics/Charts/ChangeFormulaPosition`;

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const response = await http.put(http_url, params);
        // console.log(response)
        if (response.status === 200 || response.status === 201) {
          this.setElementsUpdated(true)
          
          const genstore = generalStore()
              if (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
                genstore.selectedItem.elType === BaseItemTypes.GRAPH) {
                  this.getGraphSettings({
                    ChartId: genstore.selectedItem.Id,
                    ChartType: genstore.selectedItem.elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : (
                      genstore.selectedItem.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Linear),
                    Destination: genstore.selectedItem.DestType
                  }, genstore.selectedItem.parent)
              }

          return { response_ok: true }
        } else {
          this.setElementsUpdated(false)
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * renameGraphElement method. Backend API method for renaming a curve or a sector
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async renameGraphElement(el_id: number, newName: string, descriptor: ChartSettingsBaseDescriptor, arg: AnalyticsChartIdentity,
      no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.chartsValidation.length) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        const params = {
          Descriptor: descriptor,
          ElementId: el_id,
          NewName: newName
        }
        http_url = `Analytics/Charts/RenameElement`;

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const response = await http.put(http_url, params);
        // console.log(response)
        if (response.status === 200 || response.status === 201) {
          this.setElementsUpdated(true)
          
          const genstore = generalStore()
              if (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
                genstore.selectedItem.elType === BaseItemTypes.GRAPH) {
                  this.getGraphSettings({
                    ChartId: genstore.selectedItem.Id,
                    ChartType: genstore.selectedItem.elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : (
                      genstore.selectedItem.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Linear),
                    Destination: genstore.selectedItem.DestType
                  }, genstore.selectedItem.parent)
              }

          return { response_ok: true }
        } else {
          this.setElementsUpdated(false)
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    validateCharts(arg: any, type: number, sector: string) {
      this.chartsValidation = reactive(validateCharts(arg, type, sector))
      if (this.chartsValidation.length) {
        console.log('charts not valid')
        this.stop_request = true
      }
    },

    clearSettings() {
      this.setMutating(true)
      this.clearChartsValidation()
      // reactive filters change and update directly from interface
      this.selectedSettings = reactive({} as ChartSettingsBase);
      this.setSettingsUpdated(true)
      this.setElementsUpdated(true)
    },

  },

  getters: {
    isLoading(state): boolean {
      return state.loading
    },

    isMutating(state): boolean {
      return state.mutating
    },

    isSaving(state): boolean {
      return state.saving
    },

    getChartsValidation(state): any[] {
      return state.chartsValidation
    },

    settingsUpdated(state): boolean {
      return state.chartSettingsUpdated
    },

    elementsUpdated(state): boolean {
      return state.chartElementsUpdated
    },

    getCurrentSettings(state): ChartSettingsBase {
      return state.selectedSettings
    },

    getCurrentFormulas(state): AdaptedFormulasOption[][] {
      return state.selectedGlobalFormulas
    }
  }
});
